import { Theme, FeedTypes } from 'mewe/constants';
import { showNewToMeWePopup, showGroupPreviewDialog } from 'mewe/utils/dialogs-common';
import VideoApi from 'mewe/api/video-api';
import Storage from 'mewe/shared/storage';

const getPostScopeAndId = (post) => {
  let res = { scope: null, scopeId: null };

  if (!post) {
    return res;
  }

  if (post.eventId) {
    // post with eventId is a post inside event, post with eventInfo is a post about created event (and still can have groupId or be in home feed)
    res.scope = Theme.EVENT;
    res.scopeId = post.eventId;
  } else if (post.groupId && post.groupId !== Theme.CONTACTS) {
    res.scope = Theme.GROUP;
    res.scopeId = post.groupId;
  } else if (post.privacymail || post.threadId) {
    res.scope = Theme.PRIVATEPOSTS;
    res.scopeId = post.threadId;
  } else if (post.pageId) {
    res.scope = Theme.PAGE;
    res.scopeId = post.pageId;
  } else {
    res.scope = Theme.CONTACTS;
    res.scopeId = Theme.CONTACTS;
  }

  return res;
};

const getDisplayType = (post) => {
  if (!post) return;

  if (post.poll) {
    return 'poll';
  } else if (post.files?.length) {
    if (post.files[0].audio) {
      return 'audio';
    } else {
      return 'file';
    }
  } else if (post.link) {
    return 'link';
  }
}

const getHomeAggregatedFeed = (post) => {
  const { scope } = getPostScopeAndId(post);

  if (scope === Theme.GROUP) {
    return FeedTypes.GROUPS;
  }
  if (scope === Theme.PAGE) {
    return FeedTypes.PAGES;
  }

  return FeedTypes.CONTACTS;
};

export const getPostTheme = (post, theme) =>
  [FeedTypes.ALL, FeedTypes.DISCOVER].includes(theme) ? getPostScopeAndId(post).scope : theme;

export const postCustomInteraction = (dynamicDialogs, post, target = null, options = {}, openHashTagFromTop = null) => {
  if (post.isPublicContent) {
    showNewToMeWePopup(dynamicDialogs);
    return true;
  }

  if (shouldShowGroupPreview(post, target)) {
    showGroupPreviewDialog(dynamicDialogs, post?.group, {
      isDiscoverPostInteraction: true,
      context: options.context,
      openHashTagFromTop: openHashTagFromTop,
    });
    return true;
  }

  return false;
};

export const shouldShowGroupPreview = (post, target) => {
  if (post.discoverScope === 'group') {
    if (target) {
      // Skip group preview for specific interactions (emoji, share, undo hidden, options, translation, tag)
      if (target.closest('.c-mw-postbar-emoji') && !target.closest('.all-tab')) return;
      if (target.closest('.postbar-share-button')) return;
      if (target.closest('.post-hidden-undo')) return;
      if (target.closest('.c-mw-translation')) return;
      if (target.closest('.h-dropdown-opener') || target.closest('.c-mw-post-options')) return;
      if (target.closest('.comment-options-wrapper') || target.closest('.comment-options')) return;
      if (target.closest('.postbar-reply-button')) return;
      if (target.closest('.comment_reply')) return;
      if (target.closest('.post-media_nav-btn')) return;
      if (target.closest('.c-mw-video-player')) return;
      if (target.closest('.play-initialiser')) return;
      if (target.closest('.h-hashtag')) return;
    }

    return true;
  }

  return false;
};

export const getVideoPostJobs = async (scope, scopeId, dispatchFn) => {
  // first check if there are any pending video jobs in local memory
  const videoJobs = Storage.getParsed(Storage.keys.pendingVideoPostJobs);
  if (!Array.isArray(videoJobs)) return false;

  const isPendingFeed = !!~scopeId.indexOf('pending');
  const isScheduledFeed = !!~scopeId.indexOf('scheduled');

  // check for pending/scheduled feed was done above, now remove prefix
  // from scopeId to compare with actual ID of scope.
  scopeId = scopeId.replace('pending-', '').replace('scheduled-', '');

  // filter only video jobs for current scope
  const jobsForCurrentScope = videoJobs.filter((post) => {
    // check if pending/scheduled scope matches the post scope
    if (isPendingFeed != !!post.pending) return false;
    if (isScheduledFeed != !!post.scheduled) return false;

    const isContactsPost = !post.groupId && !post.pageId && !post.eventId && !post.threadId;

    switch (scope) {
      case FeedTypes.GROUP:
      case FeedTypes.GROUP_PENDING:
      case FeedTypes.SCHEDULED_GROUP_POSTS:
        return post.groupId === scopeId;
      case FeedTypes.GROUPS:
        return !!post.groupId;
      case FeedTypes.PAGE:
      case FeedTypes.SCHEDULED_PAGE_POSTS:
        return post.pageId === scopeId;
      case FeedTypes.PAGES:
        return !!post.pageId;
      case FeedTypes.EVENT:
      case FeedTypes.EVENT_PENDING:
        return post.eventId === scopeId;
      case FeedTypes.CONTACT: // CONTACT - profile view feed
        return post.userId === scopeId && isContactsPost;
      case FeedTypes.SCHEDULED_PROFILE_POSTS:
        return post.userId === scopeId && !post.pageId; // SCHEDULED_PROFILE_POSTS include group and profile posts, but NOT page posts
      case FeedTypes.CONTACTS: // CONTACTS - home view feed Followers
        return isContactsPost;
      case FeedTypes.ALL:
        return !post.eventId; // event posts are not included in home feed, all other scopes are
    }

    return false;
  });

  // if there are video jobs for current scope then get their status from BE
  if (jobsForCurrentScope.length) {
    const response = await VideoApi.pendingVideoJobs();
    const activeJobIds = response.jobs;
    const jobsForCurrentScopeActive = jobsForCurrentScope.filter((job) => activeJobIds.includes(job.jobId));

    // remove obsolete video jobs from local memory
    videoJobs.forEach((job) => {
      if (!activeJobIds.includes(job.jobId)) {
        dispatchFn('feed', 'removeVideoJob', job.jobId);
      }
    });

    return jobsForCurrentScopeActive;
  } else {
    return false;
  }
}

export default {
  getPostScopeAndId: getPostScopeAndId,
  getHomeAggregatedFeed: getHomeAggregatedFeed,
  getDisplayType: getDisplayType,
};
